import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { apiLogin, apiRegister, apiLogout, apiMe, apiGoogleSession, apiCompleteProfile, setToken, getToken } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = anon, object = logged in
  const [company, setCompany] = useState(null);

  const loadMe = useCallback(async () => {
    if (!getToken()) {
      setUser(false);
      return;
    }
    try {
      const data = await apiMe();
      setUser(data.user);
      setCompany(data.company);
    } catch {
      setToken(null);
      setUser(false);
      setCompany(null);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: returning from Google OAuth — AuthCallback exchanges the session_id.
    if (window.location.hash?.includes("session_id=")) return;
    loadMe();
  }, [loadMe]);

  const login = async (body) => {
    const data = await apiLogin(body);
    setToken(data.token);
    setUser(data.user);
    setCompany(data.company);
    return data;
  };

  const register = async (body) => {
    const data = await apiRegister(body);
    if (data?.token) {
      setToken(data.token);
      setUser(data.user);
      setCompany(data.company);
    }
    return data;
  };

  const googleSession = async (sessionId) => {
    const data = await apiGoogleSession(sessionId);
    setToken(data.token);
    setUser(data.user);
    setCompany(data.company);
    return data;
  };

  const completeProfile = async (body) => {
    const data = await apiCompleteProfile(body);
    setUser(data.user);
    setCompany(data.company);
    return data;
  };

  const logout = async () => {
    try { await apiLogout(); } catch { /* ignore */ }
    setToken(null);
    setUser(false);
    setCompany(null);
  };

  const refresh = loadMe;

  return (
    <AuthContext.Provider value={{ user, setUser, company, setCompany, login, register, googleSession, completeProfile, logout, refresh }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
